<script setup lang="ts">
import arrowOutward from "@/assets/image/icons/arrow_outward-lg.svg";
import type { RouteName } from "@/router";
import { useDefaultStore } from "@/store";

const store = useDefaultStore();
const revealedText = ref("");
let activeTimeouts: number[] = [];

const props = withDefaults(
  defineProps<{
    background: string;
    icon: string;
    text: string;
    alt?: string;
    active?: boolean;
    routeName: RouteName | string;
  }>(),
  {
    alt: "ICM Elektro",
  }
);

const clearAllTimeouts = () => {
  for (const id of activeTimeouts) clearTimeout(id);
  activeTimeouts = [];
};

const revealText = (text: string) => {
  clearAllTimeouts();
  revealedText.value = "";

  let i = 0;
  const typeNext = () => {
    if (i < text.length) {
      revealedText.value += text[i] === "$" ? "&shy;" : text[i];
      i++;
      const delay = text[i - 1] === " " || text[i - 1] === "$" ? 0 : 30;
      const id = globalThis.setTimeout(typeNext, delay);
      activeTimeouts.push(id);
    }
  };

  typeNext();
};

// start typing ONLY when it becomes active
watch(
  () => props.active,
  (newVal) => {
    if (newVal && props.text) {
      setTimeout(() => {
        nextTick(() => revealText(props.text));
      }, 300);
    } else {
      clearAllTimeouts();
      revealedText.value = "";
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="relative aspect-[100/134] h-full max-h-full min-w-[242px] flex-shrink-0 overflow-hidden rounded-t-[16px] rounded-b-[5px] transition-all duration-1000 ease-in-out [@media(min-width:1307px)]:aspect-auto [@media(min-width:1307px)]:min-w-0 [@media(min-width:1307px)]:cursor-pointer"
    :class="active ? '[@media(min-width:1307px)]:w-[647px]' : '[@media(min-width:1307px)]:w-[154px]'"
  >
    <!-- Image as background -->
    <img :src="background" :alt="text" class="absolute inset-0 h-full w-full object-cover object-top-left" />
    <!-- Overlay when not active -->
    <div
      class="absolute inset-0 z-1 bg-[#101010B2] transition-opacity duration-300"
      :class="active ? 'pointer-events-none opacity-0' : 'opacity-100'"
    ></div>

    <!-- Overlay text at the bottom -->
    <div
      class="bg-text-light absolute bottom-0 left-0 z-1 flex min-h-[27%] w-full items-center justify-between gap-[12px] rounded-t-[24px] p-4 duration-500 [@media(min-width:1307px)]:min-h-[124px] [@media(min-width:1307px)]:p-6"
    >
      <div class="flex w-full items-center justify-center" :class="{ hidden: (active && store.isWiderThan1307) || !store.isWiderThan1307 }">
        <img :src="icon" alt="icon" class="h-[48px] min-h-[48px] w-[48px] min-w-[48px]" />
      </div>
      <span
        ref="textContainer"
        class="text-text-dark flex-1 text-lg leading-[120%] font-semibold [@media(min-width:1307px)]:text-[32px]"
        :class="{ hidden: !active && store.isWiderThan1307 }"
        style="overflow-wrap: anywhere"
        v-html="revealedText"
      >
      </span>
      <router-link :to="{ name: routeName }" aria-label="Pogledaj više o usluzi">
        <div
          class="border-primary flex h-[72px] min-h-[72px] w-[72px] min-w-[72px] cursor-pointer items-center justify-center rounded-full border-[3px] hover:opacity-70"
          :class="{ hidden: !active && store.isWiderThan1307 }"
        >
          <div class="flex h-8 w-8 items-center justify-center">
            <img :src="arrowOutward" alt="arrow outward" />
          </div></div
      ></router-link>
    </div>
  </div>
</template>
